import type {
  AlertEnvironment, AlertKind, AlertSeverity, AlertSource, AlertStatus, ComplianceSurface, RiskTimelineEventType,
} from '@core/api/complianceTypes';
import type { PillTone } from '@shared/ui';

/** Display copy for the console's enums. Plain words, no em dashes (product copy rule). */

/**
 * What an absent value reads as on every console screen (no actor, no surface, no checker mode, a rate
 * with no denominator): a plain hyphen. The older tabs use an em dash for the same thing; the owner's
 * no-em-dash rule for user-facing text (2026-09-23) applies to everything the console renders, and
 * `screens.test.tsx` fails if the glyph reaches any of its screens.
 */
export const EMPTY = '-';

export const SEVERITY_LABEL: Record<AlertSeverity, string> = { p1: 'P1', p2: 'P2', p3: 'P3' };

/** Status colours are reserved and never colour-alone: every pill also carries its label. */
export const SEVERITY_TONE: Record<AlertSeverity, PillTone> = { p1: 'loss', p2: 'warning', p3: 'neutral' };

export const SEVERITY_TITLE: Record<AlertSeverity, string> = {
  p1: 'P1: the owner is told the same day; a supervisor note is required to resolve',
  p2: 'P2: reviewed within 2 business days',
  p3: 'P3: informational, reviewed weekly',
};

export const STATUS_LABEL: Record<AlertStatus, string> = { new: 'New', acknowledged: 'Acknowledged', resolved: 'Resolved' };
export const STATUS_TONE: Record<AlertStatus, PillTone> = { new: 'accent', acknowledged: 'warning', resolved: 'gain' };

export const SOURCE_LABEL: Record<AlertSource, string> = {
  'aws-event': 'AWS event', 'cloudwatch-alarm': 'CloudWatch alarm', 'ecs-task': 'ECS task',
  derived: 'Derived signal', manual: 'Manual',
};

export const ENVIRONMENT_LABEL: Record<AlertEnvironment, string> = { dev: 'dev', prod: 'prod', account: 'account-wide' };

export const SURFACE_LABEL: Record<ComplianceSurface, string> = {
  'day-assist-plan':   'AI Assist plan',
  'day-assist-review': 'AI Assist review',
  'day-recap':         'Day recap',
  'day-recap-email':   'Recap email',
  'day-write-up':      'Day write-up',
  'backtest-parse':    'Translation',
  'backtest-chat':     'Consult chat',
};

export const TIMELINE_LABEL: Record<RiskTimelineEventType, string> = {
  'first-attempt-block':   'First attempt withheld',
  'final-block':           'Final block (no answer shown)',
  flag:                    'Rule hit',
  'withheld-recap':        'Recap withheld',
  'flagged-recap-emailed': 'Flagged recap emailed',
  spend:                   'Spend',
  'uncharged-call':        'Absorbed call',
  'admin-grant':           'Admin credit grant',
  acceptance:              'Disclaimer accepted',
  stop:                    'Stop',
  reset:                   'Reset',
  'dark-surface-row':      'Row on a dark surface',
  'provenance-gap':        'Provenance gap',
  alert:                   'Alert',
};

/** The fixed kinds of the inventory (plan 1.1), by their A-number, for the filter and the row. */
const KIND_LABEL: Record<string, string> = {
  'trail-tampered':          'A1 Trail tampered',
  'audit-bucket-configured': 'A2 Audit bucket configured',
  'audit-object-touched':    'A3 Audit object touched',
  'breakglass-assumed':      'A4 Break-glass assumed',
  'export-task-failed':      'A5 Export task failed',
  'export-completed-absent': 'A6 Export not completed (30h)',
  /** The poller's row for a topic message it could not classify (any non-Notification body included): stored, never dropped. */
  'aws-unclassified':        'AWS message not classified',
  'marker:audit-export.gap':                   'A7 Export gap',
  'marker:audit-export.backfill-required':     'A7 Export backfill required',
  'marker:audit-export.seal-refused':          'A7 Export seal refused',
  'marker:audit-export.schema-not-current':    'A7 Export schema not current',
  'marker:audit-export.rows-disappeared':      'A7 Export rows disappeared',
  'marker:audit-export.failed':                'A7 Export failed',
  'marker:audit-export.ledger-write-failed':   'A7 Export ledger write failed',
  'marker:plan-my-day.prod-launch-blocked':    'A8 Prod launch blocked (Plan My Day)',
  'marker:ai-backtesting.prod-launch-blocked': 'A8 Prod launch blocked (AI Backtester)',
  'marker:plan-my-day.assist-blocked':         'A9 AI Assist blocks burst',
  'marker:plan-my-day.recap-withheld':         'A10 Recaps withheld',
  'marker:plan-my-day.day-summary-withheld':   'A10 Day summaries withheld',
  'marker:plan-my-day.assist-role-leak':       'A11 Role-label leaks (assist)',
  'marker:plan-my-day.day-summary-role-leak':  'A11 Role-label leaks (summary)',
  'marker:problem-response':                   'A12 AI Assist server faults',
  'marker:me.export':                          'A13 Data export burst',
  'audit-escalation':                          'A14 Monthly audit escalation',
  manual:                                      'Manual alert',
  'user-reviewed':                             'User marked reviewed',
};

/** The inbox's kind filter: the fixed kinds of the inventory plus one entry per phase-2 signal. */
export const KIND_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  ...Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
  ...['S1', 'S2', 'S3', 'S8', 'S9', 'S10', 'S11', 'S15', 'S17', 'S20'].map(s => ({ value: `signal:${s}`, label: `A15 Signal ${s}` })),
];

/** A readable name for an alert kind; unknown markers and signals fall back to their id. */
export function kindLabel(kind: AlertKind): string {
  const fixed = KIND_LABEL[kind];
  if (fixed) return fixed;
  if (kind.startsWith('signal:')) return `A15 Signal ${kind.slice('signal:'.length)}`;
  if (kind.startsWith('marker:')) return `Marker ${kind.slice('marker:'.length)}`;
  return kind;
}

/** A 0..1 fraction as "3.2%", or the placeholder when there is no denominator (never 0%). */
export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return `${(value * 100).toFixed(1)}%`;
}

/** The first 12 characters of a hash for a chip; the full hash goes in the title. */
export function shortHash(hash: string): string {
  return hash.length > 12 ? `${hash.slice(0, 12)}…` : hash;
}
